import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef
} from "react";
import { Trans, t } from "@lingui/macro";
import PropTypes from "prop-types";

import { i18n } from "base/i18n";

import { BUTTON_TYPES, Button } from "@/inputs/button";
import { EditableSingleLine } from "@/inputs/editableinput";
import { MultiLine } from "@/inputs/multiline";
import { FieldLabel, LABEL_TYPE } from "@/inputs/fields";
import { Icon, ICON_TYPES } from "@/common/components/primitives/icon";
import { Link } from "@/navigation/link";
import { Modal } from "@/datadisplay/modal";

import styles from "./flowcontainer.css";

import InfoWithCircleIcon from "./InfoWithCircle.svg";

export const MinimalFlowContainer = forwardRef((props, ref) => {
  const {
    title,
    onTitleChange,
    homeRoute,
    icon,
    children,
    showDescription = true,
    description = "",
    onDescriptionSave,
    readOnly = false
  } = props;

  const [currentTitle, setCurrentTitle] = useState(null);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [currentDescription, setCurrentDescription] = useState(null);
  const descriptionRef = useRef();
  const titleRef = useRef();
  const titleInputRef = useRef(null);

  useEffect(
    function setTitle() {
      setCurrentTitle(title);
      titleRef.current = title;
    },
    [title]
  );

  useEffect(
    function trimTitle() {
      if (!currentTitle || !currentTitle.trim()) {
        setCurrentTitle(titleRef.current);
      } else {
        titleRef.current = currentTitle;
      }
    },
    [currentTitle]
  );

  useEffect(
    function setDescription() {
      setCurrentDescription(description);
      descriptionRef.current = description;
    },
    [description]
  );

  const slots = React.Children.map(children, (child) => child);

  const actionsPanel = slots.find(
    (child) => child.type === MinimalFlowContainer.Actions
  );
  const body = slots.find((child) => child.type === MinimalFlowContainer.Body);

  const descriptionModalSaveButton = () => {
    if (readOnly) {
      return null;
    } else {
      return (
        <React.Fragment>
          <Button
            type={BUTTON_TYPES.SECONDARY}
            onClick={() => {
              setCurrentDescription(descriptionRef.current);
              setShowDescriptionModal(false);
            }}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            disabled={
              !currentDescription ||
              descriptionRef.current.trim() === currentDescription.trim()
            }
            onClick={() => {
              setShowDescriptionModal(false);
              descriptionRef.current = currentDescription;
              onDescriptionSave && onDescriptionSave(currentDescription);
            }}
          >
            <Trans>Save</Trans>
          </Button>
        </React.Fragment>
      );
    }
  };

  const modalFooterRenderer = () => {
    return null;
  };

  useImperativeHandle(ref, () => {
    return {
      setTitle: (title) => {
        titleInputRef.current.setValue(title);
      }
    };
  });

  return (
    <div className={styles.flowcontainer}>
      <div className={styles.header}>
        <div className={styles.icon}>
          <Link route={homeRoute}>{icon}</Link>
        </div>
        <div className={styles.space20} />
        <div
          className={styles.maincontainer}
          style={{ paddingBlockStart: "var(--space-24)" }}
        >
          <div className={styles.titleactioncontainer}>
            <div className={styles.titleContainer}>
              <div className={styles.titleInputContainer}>
                <EditableSingleLine
                  ref={titleInputRef}
                  value={currentTitle}
                  className={`${styles.title} h5-600`}
                  onSave={(title) => {
                    setCurrentTitle(title);
                    onTitleChange && onTitleChange(title);
                  }}
                  readOnly={readOnly}
                />
              </div>
              <div className={styles.infoIconContainer}>
                {showDescription && ((readOnly && description) || !readOnly) && (
                  <div className={styles.infoIcon}>
                    <Icon
                      type={ICON_TYPES.TYPE3}
                      paddingNone={true}
                      onClick={() => setShowDescriptionModal(true)}
                    >
                      <InfoWithCircleIcon />
                    </Icon>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.spacer} />
          </div>
        </div>
        <div className={styles.actions}>{actionsPanel}</div>
      </div>
      <div className={styles.body}>{body}</div>
      <Modal
        open={showDescriptionModal}
        title={title}
        closeOnOutsideClick={false}
        onClose={() => {
          setCurrentDescription(descriptionRef.current);
          setShowDescriptionModal(false);
        }}
        rightActionsRenderer={descriptionModalSaveButton}
        footerRenderer={readOnly ? modalFooterRenderer : null}
      >
        <div className={styles.modalDescriptionContainer}>
          <FieldLabel
            targetId="flow-description"
            label={i18n._(t`Description`)}
            labelType={LABEL_TYPE.STYLE2}
          />
          <MultiLine
            id="flow-description"
            value={currentDescription}
            readOnly={readOnly}
            disabled={readOnly}
            style={{ maxHeight: "50rem" }}
            autoGrow={true}
            onChange={(evt) => setCurrentDescription(evt.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
});

MinimalFlowContainer.propTypes = {
  title: PropTypes.string,
  onTitleChange: PropTypes.func,
  homeRoute: PropTypes.string,
  icon: PropTypes.node,
  helpContent: PropTypes.node,
  children: PropTypes.node,
  extraTabRenderer: PropTypes.func,
  readOnly: PropTypes.bool,
  showDescription: PropTypes.bool,
  description: PropTypes.string,
  onDescriptionSave: PropTypes.func
};

function FlowHeaderActionsPanel(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowHeaderActionsPanel.propTypes = {
  children: PropTypes.node
};

MinimalFlowContainer.Actions = FlowHeaderActionsPanel;
MinimalFlowContainer.Actions.displayName = "FlowContainer.Actions";

function FlowBody(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowBody.propTypes = {
  children: PropTypes.node
};

MinimalFlowContainer.Body = FlowBody;
MinimalFlowContainer.Body.displayName = "FlowContainer.Body";
